import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import AccountDetailsStep from './components/AccountDetailsStep';
import PersonalInformationStep from './components/PersonalInformationStep';
import VerificationStep from './components/VerificationStep';
import * as usersApi from 'shared/api/auth/usersApi';
import { notify } from 'shared/utils/notify';
import { Steps, Button, Alert, Flex } from 'antd';

const UserRegistrationScreen = () => {
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    // Step 1: Account Details
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    // Step 2: Personal Information
    name: '',
    surname: '',
    langId: 1 // 1 = ru, 2 = en
  });

  const steps = [
    { id: 1, title: 'Данные аккаунта', description: 'Основная информация для входа' },
    { id: 2, title: 'Личная информация', description: 'Персональные данные' },
    { id: 3, title: 'Подтверждение', description: 'Верификация email адреса' }
  ];

  const updateFormData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  };

  const handleNext = async () => {
    setError('');

    if (currentStep === 1) {
      // Капча обязательна перед переходом на шаг 2.
      const captcha = captchaRef.current?.validate();
      if (!captcha?.valid) {
        if (!captcha?.isBot) {
          notify.error('Неверный код проверки. Попробуйте ещё раз.');
        }
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Submit registration on step 2 (personal info)
      setIsLoading(true);
      try {
        console.log('Sending registration request:', {
          email: formData.email,
          password: formData.password,
          username: formData.username,
          name: formData.name,
          surname: formData.surname,
          langId: formData.langId
        });
        
        await usersApi.register({
          email: formData.email,
          password: formData.password,
          username: formData.username,
          name: formData.name,
          surname: formData.surname,
          langId: formData.langId
        });

        // Аккаунт создан, но остаётся pending_verification до перехода по ссылке из письма.
        setCurrentStep(3);
      } catch (err) {
        console.error('Registration failed:', err);
        if (err.status === 409) {
          setError('Пользователь с таким email или именем уже существует');
        } else if (err.status === 400) {
          setError(err.data?.message || err.message || 'Некорректные данные регистрации');
        } else {
          setError('Ошибка регистрации. Попробуйте позже');
        }
      } finally {
        setIsLoading(false);
      }
    } else if (currentStep === 3) {
      // Email ещё не подтверждён backend'ом — просто отправляем пользователя на вход,
      // без заявления об успешном завершении регистрации.
      navigate('/login-screen', {
        state: {
          message: 'Мы отправили письмо со ссылкой для подтверждения на ваш email. Подтвердите email, перейдя по ссылке из письма, а затем войдите в систему.',
          type: 'info'
        }
      });
    }
  };

  const handleBack = () => {
    // На шаге 1 «Назад» возвращает на экран входа,
    // на остальных шагах — переход к предыдущему шагу.
    if (currentStep === 1) {
      navigate('/login-screen');
      return;
    }
    setCurrentStep(currentStep - 1);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.email && 
               formData.username &&
               formData.username.length >= 3 &&
               formData.password && 
               formData.confirmPassword && 
               formData.password === formData.confirmPassword &&
               formData.password.length >= 8;
      case 2:
        return formData.name && 
               formData.surname;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <AccountDetailsStep
            formData={formData}
            updateFormData={updateFormData}
            captchaRef={captchaRef}
          />
        );
      case 2:
        return (
          <PersonalInformationStep 
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case 3:
        return (
          <VerificationStep formData={formData} />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Indicator */}
        <div className="mb-8">
          <Steps
            type="navigation"
            current={currentStep - 1}
            items={steps.map((step) => ({ title: step.title  }))}
          />
        </div>

        {/* Form Content */}
        <div className="bg-surface rounded-lg shadow-lg border border-secondary-200 p-6 sm:p-8">
          {error && (
            <Alert type="error" message={error} showIcon className="mb-6" />
          )}
          
          {renderStepContent()}

          {/* Navigation Buttons */}
          <Flex align="center" justify="space-between" className="mt-8 pt-6 border-t border-secondary-200">
            <Button
              onClick={handleBack}
              disabled={isLoading || currentStep === 3}
              icon={<Icon name="ChevronLeft" size={16} />}
              title={currentStep === 1 ? 'Вернуться на экран входа' : 'К предыдущему шагу'}
            >
              Назад
            </Button>

            <Button
              type="primary"
              onClick={handleNext}
              disabled={!canProceed() || isLoading}
              loading={isLoading}
              icon={currentStep === 1 && !isLoading ? <Icon name="ChevronRight" size={16} /> : undefined}
              iconPlacement="end"
            >
              {isLoading
                ? 'Обработка...'
                : currentStep === 2 ? 'Зарегистрироваться' : currentStep === 3 ? 'Перейти к входу' : 'Продолжить'}
            </Button>
          </Flex>
        </div>

        {/* Footer Links */}
        <div className="text-center mt-6">
          <Flex align="center" justify="center" className="gap-6 text-sm text-text-secondary">
            <a href="/privacy" className="hover:text-primary transition-colors duration-150">
              Политика конфиденциальности
            </a>
            <a href="/terms" className="hover:text-primary transition-colors duration-150">
              Условия использования
            </a>
            <a href="/support" className="hover:text-primary transition-colors duration-150">
              Поддержка
            </a>
          </Flex>
        </div>
      </div>
    </div>
  );
};

export default UserRegistrationScreen;